/* eslint-disable no-console */
import {
    collection,
    query,
    where,
    getDocs,
    orderBy,
    limit,
    startAfter,
    getDoc,
    doc,
    setDoc,
    updateDoc,
    writeBatch,
    arrayRemove,
    arrayUnion,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import stringify from 'json-stable-stringify';
import {
    USER_NOTIFICATION_COUNT_COLLECTION,
    NOTIFICATION_COLLECTION,
    USER_PREFERENCE_COLLECTION,
    UserType,
} from './notificationConstants';

const generateHash = (obj: any): string => {
    const str = stringify(obj);
    // Simple hash function for browser compatibility
    let hash = 0;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < (str?.length ?? 0); i++) {
        const char = str?.charCodeAt(i) ?? 0;
        // eslint-disable-next-line no-bitwise
        hash = ((hash << 5) - hash) + char;
        // eslint-disable-next-line no-bitwise
        hash &= hash;
    }
    return Math.abs(hash).toString(16);
};

interface GetNotificationDataParams {
    lastNotificationId?: string;
    userId: string;
    organisationId: string;
    userType?: string;
    authToken?: string;
}

export const getNotificationData = async (params: GetNotificationDataParams): Promise<any[]> => {
    if (!firestore) return [];

    const {
        lastNotificationId = '',
        userId,
        organisationId,
        userType = 'worker',
    } = params;

    const notificationsCollection = collection(firestore, NOTIFICATION_COLLECTION);
    const limitVal = 10;

    let showOnlyUnread = true;
    let allowedServiceNotifications: string[] | undefined;
    if (userType === UserType.WORKER) {
        showOnlyUnread = false;
        allowedServiceNotifications = ['project_x'];
    } else {
        const userPreferenceCollection = collection(firestore, USER_PREFERENCE_COLLECTION);
        const userNotificationsQuery = query(
            userPreferenceCollection,
            where('userId', '==', userId),
            where('userType', '==', userType),
            where('organisationId', '==', organisationId),
        );
        const userPreferenceSnapshot = await getDocs(userNotificationsQuery);

        if (!userPreferenceSnapshot.empty) {
            const userPreferenceData = userPreferenceSnapshot.docs[0].data();
            showOnlyUnread = userPreferenceData.showOnlyUnread;
            allowedServiceNotifications = userPreferenceData.allowedServiceNotifications;
        }
    }

    let notificationsQuery = query(
        notificationsCollection,
        where('userId', '==', userId),
        where('userType', '==', 'worker'),
        where('organisationId', '==', organisationId),
        where('isActive', '==', true),
    );

    if (allowedServiceNotifications && allowedServiceNotifications.length === 0) {
        return [];
    }

    if (showOnlyUnread) {
        notificationsQuery = query(
            notificationsQuery,
            where('isReadAll', '==', false),
        );
    }

    if (allowedServiceNotifications) {
        notificationsQuery = query(
            notificationsQuery,
            where('sourceName', 'in', allowedServiceNotifications),
        );
    }

    if (lastNotificationId) {
        const lastVisibleRef = await getDoc(
            doc(firestore, NOTIFICATION_COLLECTION, lastNotificationId),
        );
        notificationsQuery = query(
            notificationsQuery,
            orderBy('updatedAt', 'desc'),
            startAfter(lastVisibleRef),
            limit(limitVal),
        );
    } else {
        notificationsQuery = query(
            notificationsQuery,
            orderBy('updatedAt', 'desc'),
            limit(limitVal),
        );
    }

    const notifications: any[] = [];
    const notificationSnapshots = await getDocs(notificationsQuery);

    notificationSnapshots.forEach((docSnapshot) => {
        try {
            const notificationData = docSnapshot.data();
            const notification: any = {
                id: docSnapshot.id,
                ...notificationData,
            };

            if (showOnlyUnread) {
                notification.notificationHistory = notification.notificationHistory.filter(
                    (history: any) => notification.unreadIds.includes(history.id),
                );
                notification.notificationHistory.sort(
                    (a: any, b: any) => b.createdAt - a.createdAt,
                );
            } else {
                notification.notificationHistory.sort(
                    (a: any, b: any) => b.createdAt - a.createdAt,
                );
            }
            notifications.push(notification);
        } catch (error) {
            console.log('Error processing notification: ', error);
        }
    });

    return notifications;
};

interface GetUserPreferenceParams {
    userId: string;
    organisationId: string;
    userType?: string;
}

export const getUserPreference = async (params: GetUserPreferenceParams): Promise<any> => {
    if (!firestore) return {};

    const { userId, organisationId, userType = 'worker' } = params;
    const userPreferenceCollection = collection(firestore, USER_PREFERENCE_COLLECTION);
    const userNotificationsQuery = query(
        userPreferenceCollection,
        where('userId', '==', userId),
        where('userType', '==', userType),
        where('organisationId', '==', organisationId),
    );
    const userPreferenceSnapshot = await getDocs(userNotificationsQuery);

    if (userPreferenceSnapshot.empty) {
        return {};
    }

    const userPreference = userPreferenceSnapshot.docs[0].data();
    return userPreference;
};

interface ReadBellNotificationParams {
    userId: string;
    organisationId: string;
    userType?: string;
}

// TODO: Orginal function to update the notification count on opening the UI
// TODO: to be used in, instead of query-based approach using in readBellNotification
// this is temp fix to run the app without any issues
export const readBellNotificationog = async (params: ReadBellNotificationParams): Promise<void> => {
    if (!firestore) return;

    const { userId, organisationId, userType = 'worker' } = params;
    try {
        const notificationsCountCollection = collection(
            firestore,
            USER_NOTIFICATION_COUNT_COLLECTION,
        );
        const id = generateHash({ userId, userType, organisationId });
        const docRef = doc(notificationsCountCollection, id);

        const notificationsCountQuerySnapshot = await getDoc(docRef);

        if (notificationsCountQuerySnapshot.exists()) {
            await updateDoc(docRef, { count: 0 });
        } else {
            await setDoc(docRef, {
                count: 0,
                userId,
                userType,
                organisationId,
            });
        }
    } catch (error) {
        console.log('Error updating notification count: ', error);
    }
};

export const readBellNotification = async (params: ReadBellNotificationParams): Promise<void> => {
    if (!firestore) return;

    const { userId, organisationId, userType = 'worker' } = params;
    try {
        const notificationsCountCollection = collection(
            firestore,
            USER_NOTIFICATION_COUNT_COLLECTION,
        );

        // Query-based approach: find the document by field values instead of hash ID
        const countQuery = query(
            notificationsCountCollection,
            where('userId', '==', userId),
            where('userType', '==', userType),
            where('organisationId', '==', organisationId),
        );

        const querySnapshot = await getDocs(countQuery);
        console.log('querySnapshot', querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

        if (!querySnapshot.empty) {
            // Document exists - update it
            const existingDoc = querySnapshot.docs[0];
            await updateDoc(existingDoc.ref, { count: 0 });
        } else {
            // Document doesn't exist - create with auto-generated ID
            const newDocRef = doc(notificationsCountCollection);
            await setDoc(newDocRef, {
                count: 0,
                userId,
                userType,
                organisationId,
            });
        }
    } catch (error) {
        console.log('Error updating notification count: ', error);
    }
};

const validateAllowRead = (
    isRead: boolean,
    notificationData: any,
    userId: string,
    notificationHistoryId: string,
): { error: string; code: number } | null => {
    if (!notificationData) {
        return { error: 'Invalid notificationId', code: 400 };
    }
    if (notificationData.userId !== userId) {
        return { error: 'Invalid userId', code: 400 };
    }
    if (
        notificationData.notificationHistory.findIndex(
            (history: any) => history.id === notificationHistoryId,
        ) === -1
    ) {
        return { error: 'Invalid notificationHistoryId', code: 400 };
    }
    if (
        isRead
    && notificationData.unreadIds.findIndex(
        (id: string) => id === notificationHistoryId,
    ) === -1
    ) {
        return { error: 'Notification already read', code: 400 };
    }
    if (
        !isRead
    && notificationData.unreadIds.findIndex(
        (id: string) => id === notificationHistoryId,
    ) !== -1
    ) {
        return { error: 'Notification already unread', code: 400 };
    }
    return null;
};

interface ReadNotificationParams {
    organisationId: string;
    userId: string;
    userType?: string;
    notificationId?: string;
    notificationHistoryId?: string;
    isRead?: boolean;
    isReadAll: boolean;
}

export const readNotification = async (props: ReadNotificationParams): Promise<void> => {
    if (!firestore) return;

    const {
        organisationId,
        userId,
        userType = 'worker',
        notificationId,
        notificationHistoryId,
        isRead,
        isReadAll,
    } = props;

    try {
        if (isReadAll) {
            const batch = writeBatch(firestore);
            const notificationsCollection = collection(firestore, NOTIFICATION_COLLECTION);
            const notificationsQuery = query(
                notificationsCollection,
                where('userId', '==', userId),
                where('userType', '==', userType),
                where('organisationId', '==', organisationId),
                where('isActive', '==', true),
                where('isReadAll', '==', false),
            );
            const notificationSnapshots = await getDocs(notificationsQuery);

            notificationSnapshots.forEach((documnent) => {
                const notificationRef = doc(notificationsCollection, documnent.id);
                batch.update(notificationRef, {
                    unreadIds: [],
                    isReadAll: true,
                });
            });
            await batch.commit();
        } else {
            if (!notificationId || !notificationHistoryId) {
                console.log('Invalid notificationId or notificationHistoryId');
                return;
            }
            const notificationRef = doc(
                collection(firestore, NOTIFICATION_COLLECTION),
                notificationId,
            );
            const notificationSnapshot = await getDoc(notificationRef);
            const notificationData = notificationSnapshot.data();

            const error = validateAllowRead(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                isRead!,
                notificationData,
                userId,
                notificationHistoryId,
            );

            if (!error) {
                let isReadAllNotifications = notificationData?.isReadAll;

                if (isRead && notificationData?.unreadIds.length === 1) {
                    isReadAllNotifications = true;
                }
                if (!isRead && isReadAllNotifications) {
                    isReadAllNotifications = false;
                }

                const unreadIds = isRead
                    ? arrayRemove(notificationHistoryId)
                    : arrayUnion(notificationHistoryId);

                await updateDoc(notificationRef, {
                    unreadIds,
                    isReadAll: isReadAllNotifications,
                });
            } else {
                console.log('error', error);
            }
        }
    } catch (error) {
        console.log('Error updating notifications: ', error);
    }
};
